'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { getFacetTreeIdPrefix } from '../../facetTypes';
import { getDateRangeDays } from '../../utils/facetLabels';
import { DATE_RANGE_KEYS } from '../../utils/facetRanges';
import { FlatFacetShell } from '../FlatFacetShell';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { DateFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { ReleaseDateFacetProps } from './ReleaseDateFacet.types';

const getValueId = (v: DateFlatFacetValue): string => `${v.start}-${v.end}`;

export const ReleaseDateFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: ReleaseDateFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const dateLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filter-release-date-label',
    'Release Date'
  );

  const clearLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.clear',
    'Clear'
  );

  const firstPublishedAtBundle = useMemo(
    () =>
      microcopySetToResourceBundles(searchValuesMicrocopy ?? [])
        .first_published_at,
    [searchValuesMicrocopy]
  );

  const controllers = useSearchControllers();
  const releaseDateFacet = controllers.useReleaseDateFacet();
  const {
    values,
    selectedIds,
    selectedCount,
    isReady,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({ facet: releaseDateFacet, getValueId });

  const isDark = variant === 'mobile';

  // The engine defines the windows as relative ranges (`past-N-day`..`now`),
  // and that is the form controller state hands back — never positional, so
  // each value is matched to its DATE_RANGE_KEYS entry by its duration in
  // days, then ordered shortest window first.
  const treeItems = useMemo(
    () =>
      values
        .map((v) => ({ value: v, days: getDateRangeDays(v.start, v.end) }))
        .sort((a, b) => a.days - b.days)
        .map(({ value: v, days }) => {
          const range = DATE_RANGE_KEYS.find((r) => r.days === days);
          return {
            id: getValueId(v),
            label: range
              ? (firstPublishedAtBundle?.[range.bundleKey] ?? range.label)
              : `${v.start} – ${v.end}`,
            count: v.numberOfResults,
            disabled:
              !isReady || (v.numberOfResults === 0 && v.state !== 'selected'),
          };
        }),
    [values, firstPublishedAtBundle, isReady]
  );

  return (
    <FlatFacetShell
      title={dateLabel}
      clearLabel={clearLabel}
      selectedCount={selectedCount}
      onClear={handleClear}
      variant={variant}
      className={className}
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={!isReady}
    >
      <TreeAccordion
        idPrefix={getFacetTreeIdPrefix(variant)}
        items={treeItems}
        selectionMode="multiple"
        selectedIds={selectedIds}
        onSelectMany={handleSelectMany}
        isDarkTheme={isDark}
        size={variant === 'sidebar' ? 'default' : 'sm'}
        reserveExpandSpace={variant !== 'horizontal'}
      />
    </FlatFacetShell>
  );
};
